// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { rem } from "polished";
import type { JSX } from "react";
import * as React from "react";

import { ButtonProps } from "../Button";
import { Icon } from "../Icon";
import { CaretWrapper, ToggleElement } from "./Dropdown.styles";
import DropdownContext from "./DropdownContext";

export interface DropdownToggleProps extends ButtonProps {
  children?: React.ReactNode;
  className?: string;
  showCaret?: boolean;
}

export const DropdownToggle = ({
  children,
  shape = "block",
  kind = "secondary",
  showCaret,
  style,
  ...attributes
}: DropdownToggleProps): JSX.Element => {
  const { shown, setShown } = React.useContext(DropdownContext);

  const inlineStyles = { ...style };
  if (showCaret) {
    inlineStyles.justifyContent = "space-between";
  }

  return (
    <ToggleElement
      onClick={() => setShown(!shown)}
      onKeyPress={(event) => {
        if (event.key === "Down" || event.key === "ArrowDown") {
          setShown(true);
        }
      }}
      aria-haspopup="menu"
      aria-expanded={shown}
      shape={shape}
      kind={kind}
      tabIndex={shown ? -1 : 0}
      style={inlineStyles}
      {...attributes}
    >
      {children}
      {showCaret && (
        <CaretWrapper>
          <Icon kind="DownChevron" height={rem(4)} width={rem(8)} />
        </CaretWrapper>
      )}
    </ToggleElement>
  );
};
